import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCheck } from '@fortawesome/free-solid-svg-icons';
import { EditTaskForm } from '../forms';
import { ProjectNameWithStatus } from '../helpers/ProjectNameWithStatus';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
};

const getDaysUntilDue = (dateString) => {
    if (!dateString) return null;
    const dueDate = new Date(dateString);
    if (isNaN(dueDate.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getDueDateStatus = (task) => {
    if (task.status === 'Completed') return 'completed';

    const daysUntil = getDaysUntilDue(task.due_date);
    if (daysUntil === null) return 'no-date';
    if (daysUntil < 0) return 'overdue';
    if (daysUntil === 0) return 'due-today';
    if (daysUntil <= 3) return 'due-soon';
    return 'on-track';
};

const getDueDateLabel = (task) => {
    if (task.status === 'Completed') return null;

    const daysUntil = getDaysUntilDue(task.due_date);
    if (daysUntil === null) return null;
    if (daysUntil < 0) return `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''}`;
    if (daysUntil === 0) return 'Due Today';
    if (daysUntil === 1) return 'Due Tomorrow';
    if (daysUntil <= 7) return `Due in ${daysUntil} days`;
    return null;
};

const getTaskScope = (task) => {
    if (task.design_unique_id) {
        return {
            label: `Design: ${task.design_unique_id}`,
            className: 'status-chip status-chip--scope-design',
            cardClassName: 'scope-design',
        };
    }

    return {
        label: 'Project',
        className: 'status-chip status-chip--scope-project',
        cardClassName: 'scope-project',
    };
};

const DUE_DATE_CHIP = {
    overdue: 'status-chip--danger',
    'due-today': 'status-chip--due-today',
    'due-soon': 'status-chip--warning',
};

const TASK_STATUS_CHIP = {
    'not-started': 'status-chip--neutral',
    'in-progress': 'status-chip--progress',
    completed: 'status-chip--success',
    'on-hold': 'status-chip--warning',
};

/**
 * Shared task card feed used by the Tasks page and project Tasks tab.
 *
 * @param {Object} props
 * @param {Array} props.tasks - Tasks to render (already filtered/sorted by parent)
 * @param {string} [props.emptyTitle='No tasks found.']
 * @param {string} [props.emptyMessage]
 * @param {boolean} [props.showAssignee=true] - Show assignee in card footer
 * @param {boolean} [props.showProjectLink=false] - Show project name link
 * @param {Object} [props.project] - Fallback project for EditTaskForm when task.project is missing
 */
export const TasksFeed = ({
    tasks = [],
    emptyTitle = 'No tasks found.',
    emptyMessage = '',
    showAssignee = true,
    showProjectLink = false,
    project = null,
}) => {
    const navigate = useNavigate();

    const handleProjectClick = (projectId) => {
        navigate(`/project/${projectId}?tab=tasks`);
    };

    if (!tasks.length) {
        return (
            <div className="empty-state">
                <p>{emptyTitle}</p>
                {emptyMessage && <p>{emptyMessage}</p>}
            </div>
        );
    }

    return (
        <div className="tasks-feed-list">
            {tasks.map((task, index) => {
                const dueDateStatus = getDueDateStatus(task);
                const dueDateLabel = getDueDateLabel(task);
                const taskScope = getTaskScope(task);
                const taskProject = task.project || project;
                const projectId = task.project_id ?? taskProject?.id;

                return (
                    <div
                        key={`${projectId ?? 'task'}-${task.task_name}-${index}`}
                        className={`task-card ${dueDateStatus} ${taskScope.cardClassName}`}
                    >
                        <div className="task-header">
                            <div className="task-title-section">
                                <h3>
                                    {dueDateStatus === 'completed' && (
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="task-complete-icon"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <span className="task-title-text">{task.task_name}</span>
                                </h3>
                                {showProjectLink && taskProject && projectId != null && (
                                    <button
                                        type="button"
                                        className="task-project-link"
                                        onClick={() => handleProjectClick(projectId)}
                                        title={`Go to project #${projectId}`}
                                    >
                                        <ProjectNameWithStatus project={taskProject} />
                                        <span className="task-project-id">
                                            (#{projectId})
                                        </span>
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                            className="project-link-icon"
                                        />
                                    </button>
                                )}
                                <div className="task-scope-row">
                                    <span className={taskScope.className}>{taskScope.label}</span>
                                </div>
                            </div>
                            <div className="task-status-section">
                                {dueDateLabel && (
                                    <span className={`status-chip ${DUE_DATE_CHIP[dueDateStatus] || 'status-chip--neutral'}`}>
                                        {dueDateLabel}
                                    </span>
                                )}
                                <span className={`status-chip ${TASK_STATUS_CHIP[task.status?.toLowerCase().replace(/\s+/g, '-')] || 'status-chip--neutral'}`}>
                                    {task.status || 'Not Started'}
                                </span>
                            </div>
                        </div>

                        {task.task_description && (
                            <p className="task-description">{task.task_description}</p>
                        )}

                        {task.note && (
                            <div className="task-handoff-note">
                                <span className="task-handoff-label">Handoff note</span>
                                <p className="task-handoff-body">{task.note}</p>
                            </div>
                        )}

                        {task.response && (
                            <div className="task-handoff-note">
                                <span className="task-handoff-label">Completion response</span>
                                <p className="task-handoff-body">{task.response}</p>
                            </div>
                        )}

                        <div className="task-footer">
                            <div className="task-meta">
                                <span className="task-due-date">
                                    Due: {formatDate(task.due_date)}
                                </span>
                                {showAssignee && (
                                    <>
                                        <span className="task-assignee">
                                            Assigned to: {task.assigned_to || 'Unassigned'}
                                        </span>
                                        <span className="task-assigner">
                                            Assigned by: {task.created_by || 'Unknown'}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="task-actions">
                                {taskProject && (
                                    <EditTaskForm project={taskProject} task={task} />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
